import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Check, AlertCircle, Loader2, Eraser } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

interface Employee {
  first_name: string;
  last_name: string;
  role: string;
  hours_worked: number;
}

interface Equipment {
  equipment_name: string;
  equipment_type: string;
  hours_used: number;
  is_rental: boolean;
  rental_company?: string;
  is_mobilized?: boolean;
}

interface Material {
  material_type: 'import' | 'export';
  description: string;
  quantity: number;
  unit: string;
}

interface Dumpster {
  dumpster_type: string;
  quantity: number;
}

interface TicketData {
  ticket_id: number;
  ticket_number: string;
  job_name: string;
  bradanna_job_number: string;
  superintendent_name: string;
  superintendent_email: string;
  work_description: string;
  notes: string;
  start_time: string;
  stop_time: string;
  total_employee_hours: number;
  total_equipment_hours: number;
  gc_name: string;
  created_at: string;
  submitter_first_name?: string;
  submitter_last_name?: string;
  employees: Employee[];
  equipment: Equipment[];
  materials?: Material[];
  dumpsters?: Dumpster[];
}

// Format role for display
const formatRole = (role: string): string => {
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const RemoteSign: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [ticket, setTicket] = useState<TicketData | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastPosRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    loadTicket();
  }, [token]);

  // Scroll to top when loaded
  useEffect(() => {
    if (ticket) {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      if (topRef.current) {
        topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    }
  }, [ticket]);

  // Setup canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * 2;
    canvas.height = rect.height * 2;
    ctx.scale(2, 2);

    ctx.strokeStyle = '#1f2937';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, [ticket]);

  const loadTicket = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/remote-sign/${token}`);
      const result = await response.json();

      if (result.success) {
        setTicket(result.data);
      } else {
        setError(result.error || 'Invalid or expired link');
      }
    } catch (err) {
      setError('Failed to load ticket');
    } finally {
      setLoading(false);
    }
  };

  const getCanvasPosition = (e: React.TouchEvent | React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;

    return {
      x: clientX - rect.left,
      y: clientY - rect.top
    };
  };

  const startDrawing = (e: React.TouchEvent | React.MouseEvent) => {
    e.preventDefault();
    setIsDrawing(true);
    const pos = getCanvasPosition(e);
    lastPosRef.current = pos;
    setHasSignature(true);
  };

  const draw = (e: React.TouchEvent | React.MouseEvent) => {
    if (!isDrawing) return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !lastPosRef.current) return;

    const pos = getCanvasPosition(e);

    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastPosRef.current = pos;
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    lastPosRef.current = null;
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasSignature(false);
  };

  const getSignatureBase64 = (): string => {
    const canvas = canvasRef.current;
    if (!canvas) return '';
    return canvas.toDataURL('image/png');
  };

  const handleSubmit = async () => {
    if (!hasSignature) {
      setError('Please sign the ticket');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const signatureBase64 = getSignatureBase64();

      const response = await fetch(`${API_BASE_URL}/api/tm-tickets/remote-sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_base64: signatureBase64 })
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.error || 'Failed to submit signature');
      }
    } catch (err) {
      setError('Failed to submit signature');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (time: string | null) => {
    if (!time) return '--';
    return new Date(time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
        padding: '20px'
      }}>
        <Loader2 size={48} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
        <p style={{ marginTop: '16px', color: '#6b7280' }}>Loading ticket...</p>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error && !ticket) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
        padding: '20px'
      }}>
        <AlertCircle size={64} color="#ef4444" />
        <h1 style={{ marginTop: '16px', fontSize: '24px', color: '#1f2937' }}>Link Invalid or Expired</h1>
        <p style={{ marginTop: '8px', color: '#6b7280', textAlign: 'center' }}>{error}</p>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f3f4f6',
        padding: '20px'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#22c55e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <Check size={48} color="white" />
        </div>
        <h1 style={{ marginTop: '24px', fontSize: '28px', color: '#166534', fontWeight: '700' }}>
          Ticket Signed!
        </h1>
        <p style={{ marginTop: '12px', color: '#6b7280', textAlign: 'center' }}>
          Thank you for signing {ticket?.ticket_number}.<br />
          You may close this window.
        </p>
      </div>
    );
  }

  const importMaterials = ticket?.materials?.filter(m => m.material_type === 'import') || [];
  const exportMaterials = ticket?.materials?.filter(m => m.material_type === 'export') || [];

  return (
    <div ref={topRef} style={{
      backgroundColor: '#f3f4f6',
      minHeight: '100vh',
      padding: '16px',
      paddingBottom: '100px', // Extra padding for submit button visibility
      overflowY: 'auto'
    }}>
      {/* Preview Container - Paper document style */}
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        backgroundColor: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        {/* Black Header */}
        <div style={{
          backgroundColor: '#000000',
          color: 'white',
          padding: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '24px', fontWeight: '700', letterSpacing: '1px' }}>
            BRADANNA
          </div>
          <div style={{ textAlign: 'right', fontSize: '12px', lineHeight: '1.5' }}>
            <div>4949 Tamiami Trail N, Suite 208</div>
            <div>Naples, FL 34103</div>
            <div>239-455-8891</div>
          </div>
        </div>

        {/* Red Title Bar */}
        <div style={{
          backgroundColor: '#dc2626',
          color: 'white',
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: '700',
          letterSpacing: '1px',
          textAlign: 'center'
        }}>
          TIME & MATERIALS TICKET
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '12px 20px',
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={20} color="#ef4444" />
            <span style={{ color: '#991b1b', fontSize: '14px' }}>{error}</span>
          </div>
        )}

        {/* Ticket Info Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              TICKET NUMBER
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#dc2626' }}>
              {ticket?.ticket_number}
            </div>
          </div>
          <div style={{ padding: '12px 20px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              DATE SUBMITTED
            </div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
              {ticket?.created_at ? formatDate(ticket.created_at) : '--'}
            </div>
          </div>
        </div>

        {/* Job Site Row */}
        <div style={{
          padding: '12px 20px',
          borderBottom: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
            JOB SITE
          </div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
            {ticket?.job_name}
          </div>
          {ticket?.bradanna_job_number && (
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              #{ticket.bradanna_job_number}
            </div>
          )}
        </div>

        {/* GC & Superintendent Row */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: ticket?.gc_name ? '1fr 1fr' : '1fr',
          borderBottom: '1px solid #e5e7eb'
        }}>
          {ticket?.gc_name && (
            <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
                GENERAL CONTRACTOR
              </div>
              <div style={{ fontSize: '15px', fontWeight: '600', color: '#166534' }}>
                {ticket.gc_name}
              </div>
            </div>
          )}
          <div style={{ padding: '12px 20px' }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              SUPERINTENDENT
            </div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
              {ticket?.superintendent_name}
            </div>
            {ticket?.superintendent_email && (
              <div style={{ fontSize: '13px', color: '#6b7280' }}>
                {ticket.superintendent_email}
              </div>
            )}
          </div>
        </div>

        {/* Submitted By Row */}
        {(ticket?.submitter_first_name || ticket?.submitter_last_name) && (
          <div style={{
            padding: '12px 20px',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
              SUBMITTED BY
            </div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937' }}>
              {ticket.submitter_first_name} {ticket.submitter_last_name}
            </div>
          </div>
        )}

        {/* Start/Stop Times */}
        {(ticket?.start_time || ticket?.stop_time) && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <div style={{ padding: '12px 20px', borderRight: '1px solid #e5e7eb' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
                START TIME
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                {formatTime(ticket?.start_time)}
              </div>
            </div>
            <div style={{ padding: '12px 20px' }}>
              <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', marginBottom: '4px' }}>
                STOP TIME
              </div>
              <div style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                {formatTime(ticket?.stop_time)}
              </div>
            </div>
          </div>
        )}

        {/* Work Description */}
        {ticket?.work_description && (
          <div style={{
            padding: '12px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <div style={{ fontSize: '11px', color: '#000', fontWeight: '600', marginBottom: '8px' }}>
              SCOPE OF WORK
            </div>
            <div style={{
              fontSize: '14px',
              color: '#000',
              lineHeight: '1.5',
              whiteSpace: 'pre-wrap'
            }}>
              {ticket.work_description}
            </div>
          </div>
        )}

        {/* Hours Summary - Muted grey style */}
        <div style={{
          display: 'flex',
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: 'rgba(0,0,0,0.02)',
          gap: '40px'
        }}>
          {/* Employee Hours */}
          <div>
            <div style={{ fontSize: '11px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>
              TOTAL EMPLOYEE HOURS
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
              {(ticket?.total_employee_hours || 0).toFixed(1)}
            </div>
          </div>

          {/* Equipment Hours */}
          <div>
            <div style={{ fontSize: '11px', color: '#374151', fontWeight: '600', marginBottom: '4px' }}>
              TOTAL EQUIPMENT HOURS
            </div>
            <div style={{ fontSize: '18px', fontWeight: '700', color: '#374151' }}>
              {(ticket?.total_equipment_hours || 0).toFixed(1)}
            </div>
          </div>
        </div>

        {/* Employees Table */}
        {ticket?.employees && ticket.employees.length > 0 && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              EMPLOYEES ({ticket.employees.length})
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>NAME</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>ROLE</th>
                  <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>HOURS</th>
                </tr>
              </thead>
              <tbody>
                {ticket.employees.map((emp, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 1 ? '#f9fafb' : 'white' }}>
                    <td style={{ padding: '10px 20px', fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                      {emp.first_name} {emp.last_name}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '14px', color: '#6b7280' }}>
                      {formatRole(emp.role)}
                    </td>
                    <td style={{ padding: '10px 20px', textAlign: 'right', fontSize: '14px', color: '#22c55e', fontWeight: '600' }}>
                      {emp.hours_worked.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Equipment Table */}
        {ticket?.equipment && ticket.equipment.length > 0 && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              EQUIPMENT ({ticket.equipment.length})
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f3f4f6' }}>
                  <th style={{ padding: '10px 20px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>EQUIPMENT</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>TYPE</th>
                  <th style={{ padding: '10px 20px', textAlign: 'right', fontSize: '11px', color: '#6b7280', fontWeight: '600' }}>HOURS</th>
                </tr>
              </thead>
              <tbody>
                {ticket.equipment.map((eq, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 1 ? '#f9fafb' : 'white' }}>
                    <td style={{ padding: '10px 20px', fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                      {eq.equipment_name}
                      {eq.is_rental && (
                        <span style={{
                          marginLeft: '8px',
                          backgroundColor: '#f59e0b',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          RENTAL
                        </span>
                      )}
                      {eq.is_mobilized && (
                        <span style={{
                          marginLeft: '8px',
                          backgroundColor: '#3b82f6',
                          color: 'white',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          MOBILIZED
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '14px', color: '#6b7280' }}>
                      {eq.equipment_type}
                    </td>
                    <td style={{ padding: '10px 20px', textAlign: 'right', fontSize: '14px', color: '#f59e0b', fontWeight: '600' }}>
                      {eq.hours_used.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Materials Section */}
        {(importMaterials.length > 0 || exportMaterials.length > 0 || (ticket?.dumpsters && ticket.dumpsters.length > 0)) && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              MATERIALS
            </div>
            <div style={{ padding: '16px 20px' }}>
              {importMaterials.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>IMPORTS:</div>
                  {importMaterials.map((mat, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#f0fdf4',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#166534',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{mat.description}</span>
                      <span style={{ fontWeight: '600' }}>{mat.quantity} {mat.unit}</span>
                    </div>
                  ))}
                </div>
              )}
              {exportMaterials.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>EXPORTS:</div>
                  {exportMaterials.map((mat, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#fef2f2',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#991b1b',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{mat.description}</span>
                      <span style={{ fontWeight: '600' }}>{mat.quantity} {mat.unit}</span>
                    </div>
                  ))}
                </div>
              )}
              {ticket?.dumpsters && ticket.dumpsters.length > 0 && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#7c3aed', marginBottom: '8px' }}>DUMPSTERS:</div>
                  {ticket.dumpsters.map((dump, index) => (
                    <div key={index} style={{
                      padding: '8px 12px',
                      backgroundColor: '#f5f3ff',
                      borderRadius: '6px',
                      marginBottom: '6px',
                      fontSize: '14px',
                      color: '#7c3aed',
                      display: 'flex',
                      justifyContent: 'space-between'
                    }}>
                      <span>{dump.dumpster_type}</span>
                      <span style={{ fontWeight: '600' }}>Qty: {dump.quantity}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notes Section */}
        {ticket?.notes && (
          <div style={{ borderBottom: '1px solid #e5e7eb' }}>
            <div style={{
              padding: '12px 20px',
              backgroundColor: '#f9fafb',
              borderBottom: '1px solid #e5e7eb',
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              NOTES
            </div>
            <div style={{
              padding: '16px 20px',
              fontSize: '14px',
              color: '#374151',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap'
            }}>
              {ticket.notes}
            </div>
          </div>
        )}

        {/* Superintendent Signature Section */}
        <div style={{ padding: '20px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div style={{
              fontSize: '13px',
              fontWeight: '700',
              color: '#374151'
            }}>
              SUPERINTENDENT SIGNATURE
            </div>
            {hasSignature && (
              <button
                onClick={clearSignature}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Eraser size={16} />
                Clear
              </button>
            )}
          </div>

          <p style={{ margin: '0 0 12px', fontSize: '14px', color: '#6b7280' }}>
            Hi {ticket?.superintendent_name}, please sign below to confirm this work.
          </p>

          {/* Signature Canvas */}
          <div style={{
            border: '2px solid #d1d5db',
            borderRadius: '8px',
            backgroundColor: '#f9fafb',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <canvas
              ref={canvasRef}
              style={{
                width: '100%',
                height: '150px',
                touchAction: 'none',
                cursor: 'crosshair'
              }}
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
            />
          </div>

          {/* Signature Line */}
          <div style={{ textAlign: 'center', marginTop: '8px' }}>
            <div style={{
              borderTop: '1px solid #9ca3af',
              width: '80%',
              margin: '0 auto 8px',
              paddingTop: '8px',
              fontSize: '12px',
              color: '#6b7280'
            }}>
              Authorized Signature
            </div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>
              Date: {ticket?.created_at ? formatDate(ticket.created_at) : '--'}
            </div>
          </div>

          {/* Signature Required Message */}
          {!hasSignature && (
            <div style={{
              textAlign: 'center',
              marginTop: '12px',
              fontSize: '13px',
              color: '#dc2626',
              fontStyle: 'italic'
            }}>
              Please sign above to submit
            </div>
          )}
        </div>

        {/* Submit Button */}
        <div style={{
          padding: '20px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <button
            onClick={handleSubmit}
            disabled={submitting || !hasSignature}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: (submitting || !hasSignature) ? '#9ca3af' : '#22c55e',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '700',
              cursor: (submitting || !hasSignature) ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {submitting ? (
              <>
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                Submitting...
              </>
            ) : (
              <>
                <Check size={20} />
                Submit Signed Ticket
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default RemoteSign;
